import { Link } from 'react-router-dom';
import { useState, type FormEvent } from 'react';
import {
  useAssets,
  useAssetDetail,
  useCreateAsset,
  useAssignAsset,
  useUnassignAsset,
  useDeleteAsset,
  useRestoreAsset,
  type AssetStatusFilter,
} from '../api/queries/assets';
import { useExpenses, useUpdateExpense } from '../api/queries/expenses';
import { useAssignableUsers } from '../api/queries/project-tasks';
import { ApiError } from '../api/client';
import { apiErrorText } from '../utils/apiErrors';
import { can } from '../auth/store';
import { fmtDate } from '../utils/date';
import { DateInput } from '../components/DateInput';
import { MoneyInput } from '../components/MoneyInput';
import { LookupSelect } from '../components/LookupSelect';
import { IconAction } from '../components/IconAction';
import { Modal } from '../components/Modal';
import { EditAssetModal } from '../components/EditAssetModal';
import { RetireAssetModal } from '../components/RetireAssetModal';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import { useLookupLabel } from '../api/queries/lookups';
import type { Asset, AssetType } from '@stfors/shared-types';
import './AssetsPage.css';

type AssetSortKey = 'name' | 'type' | 'serial' | 'location' | 'assigned' | 'warranty' | 'expenses';

import { formatMoney } from '../utils/money';
import { Toggle } from '../components/Toggle';

const STATUS_TABS: { key: AssetStatusFilter; label: string }[] = [
  { key: 'in_inventory', label: 'In inventory' },
  { key: 'sold', label: 'Sold' },
  { key: 'disposed', label: 'Disposed' },
  { key: 'all', label: 'All' },
];

function emptyText(tab: AssetStatusFilter, unassignedOnly: boolean): string {
  if (unassignedOnly) return 'No unassigned assets here.';
  switch (tab) {
    case 'in_inventory': return 'No assets in inventory.';
    case 'sold': return 'No assets sold.';
    case 'disposed': return 'No assets disposed of.';
    case 'all': return 'No assets tracked.';
  }
}

export function AssetsPage(): JSX.Element {
  const [typeFilter, setTypeFilter] = useState<AssetType | ''>('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  // In inventory by default: equipment the firm no longer has (sold, or
  // disposed of — 0060) is one click away, not mixed into the list.
  const [statusTab, setStatusTab] = useState<AssetStatusFilter>('in_inventory');

  const { data, isPending, error } = useAssets({
    ...(typeFilter ? { asset_type: typeFilter } : {}),
    unassigned: unassignedOnly || undefined,
    status: statusTab,
  });
  const restoreAsset = useRestoreAsset();
  /** Asset whose Retire window is open (0060). */
  const [retiring, setRetiring] = useState<Asset | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const usersQuery = useAssignableUsers();
  const createAsset = useCreateAsset();
  const assignAsset = useAssignAsset();
  const unassignAsset = useUnassignAsset();
  const deleteAsset = useDeleteAsset();
  const updateExpense = useUpdateExpense();
  const typeLabel = useLookupLabel('asset_type');
  /** Asset whose Edit modal is open. */
  const [editAssetId, setEditAssetId] = useState<number | null>(null);
  /** Asset whose linked expenses modal is open. */
  const [expensesAssetId, setExpensesAssetId] = useState<number | null>(null);
  const [linkExpenseId, setLinkExpenseId] = useState<number | ''>('');
  const [linkError, setLinkError] = useState<string | null>(null);

  const assetDetailQ = useAssetDetail(expensesAssetId);
  // Unlinked expenses for the "link expense" picker (only when modal open).
  const unlinkedExpensesQ = useExpenses(
    expensesAssetId !== null && can('expense.read') ? {} : undefined,
  );

  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<AssetType>('write_blocker');
  const [newSerial, setNewSerial] = useState('');
  const [newMakeModel, setNewMakeModel] = useState('');
  const [newPurchaseDate, setNewPurchaseDate] = useState<number | null>(null);
  const [newPurchasePrice, setNewPurchasePrice] = useState('');
  const [newWarranty, setNewWarranty] = useState<number | null>(null);
  const [newLocation, setNewLocation] = useState('');
  const [newAssignee, setNewAssignee] = useState<number | ''>('');
  const [newNotes, setNewNotes] = useState('');
  const [newExpenseId, setNewExpenseId] = useState<number | ''>('');
  const [createError, setCreateError] = useState<string | null>(null);
  // "Duplicate and edit": the name of the asset the open form was copied from.
  const [dupFrom, setDupFrom] = useState<string | null>(null);

  /** Blank the New Asset form — so a cancelled duplicate never leaks into the next "New asset". */
  const resetNew = (): void => {
    setNewName('');
    setNewType('write_blocker');
    setNewSerial('');
    setNewMakeModel('');
    setNewPurchaseDate(null);
    setNewPurchasePrice('');
    setNewWarranty(null);
    setNewLocation('');
    setNewAssignee('');
    setNewNotes('');
    setNewExpenseId('');
    setDupFrom(null);
    setCreateError(null);
  };

  /**
   * Open the New Asset form pre-filled from `a`. Nothing is saved until the
   * user saves. Not copied: the serial number (it identifies ONE device), who
   * it's assigned to, and the linked purchase expense — an expense belongs to
   * one asset, and its cost is that asset's cost.
   */
  const startDuplicate = (a: Asset): void => {
    resetNew();
    setNewName(a.name);
    setNewType(a.asset_type);
    setNewMakeModel(a.make_model ?? '');
    setNewPurchaseDate(a.purchase_date);
    setNewPurchasePrice(a.purchase_price ?? '');
    setNewWarranty(a.warranty_expiry);
    setNewLocation(a.location ?? '');
    setNewNotes(a.notes ?? '');
    setDupFrom(a.name);
    setShowNew(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const allExpensesForLink = useExpenses(can('expense.read') ? {} : undefined);
  const expensesForPicker = (allExpensesForLink.data?.expenses ?? []).filter(
    (e) => e.asset_id == null || e.asset_id === 0,
  );

  // Inline assign dropdown state, keyed by asset id.
  const [assignBoxFor, setAssignBoxFor] = useState<number | null>(null);
  const [assignTarget, setAssignTarget] = useState<number | ''>('');
  const [assignError, setAssignError] = useState<string | null>(null);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (newName.trim().length === 0) return;
    try {
      const created = await createAsset.mutateAsync({
        name: newName.trim(),
        asset_type: newType,
        serial_number: newSerial.trim() || undefined,
        make_model: newMakeModel.trim() || undefined,
        purchase_date: newPurchaseDate ?? undefined,
        purchase_price: newPurchasePrice.trim() || undefined,
        warranty_expiry: newWarranty ?? undefined,
        location: newLocation.trim() || undefined,
        assigned_to_user_id: newAssignee === '' ? undefined : newAssignee,
        notes: newNotes.trim() || undefined,
      });
      // Optional link: stamp expense.asset_id after the asset exists.
      if (newExpenseId !== '' && can('expense.write')) {
        try {
          await updateExpense.mutateAsync({
            id: newExpenseId,
            asset_id: created.asset.id,
          });
        } catch (linkErr) {
          // Asset was created; surface link failure without rolling back.
          setCreateError(
            `Asset saved, but linking the expense failed: ${apiErrorText(linkErr, 'unknown error')}. ` +
            'Link it from the asset’s expenses list.',
          );
          setShowNew(false);
          return;
        }
      }
      setShowNew(false);
      resetNew();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setCreateError("You don't have permission to add assets.");
        return;
      }
      // apiErrorText folds zod fieldErrors into the banner itself, so the
      // hand-rolled dig through details.fieldErrors that used to live here is
      // redundant — and it discarded root-level errors, which is how a rejected
      // asset_type read as "Could not save. Try again."
      setCreateError(apiErrorText(err, 'Could not save the asset.'));
    }
  };

  const onAssign = async (assetId: number) => {
    if (assignTarget === '') return;
    setAssignError(null);
    try {
      await assignAsset.mutateAsync({ id: assetId, userId: assignTarget });
      setAssignBoxFor(null);
      setAssignTarget('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setAssignError("You don't have permission to assign assets.");
        return;
      }
      setAssignError(apiErrorText(err, 'Could not assign the asset.'));
    }
  };

  const assets = data?.assets ?? [];
  const users = usersQuery.data?.users ?? [];
  const sort = useClientSort(assets, (a, key: AssetSortKey) => {
    switch (key) {
      case 'name': return a.name;
      case 'type': return typeLabel(a.asset_type);
      case 'serial': return a.serial_number ?? '';
      case 'location': return a.location ?? '';
      case 'assigned': return a.assigned_to_name ?? '';
      case 'warranty': return a.warranty_expiry ?? null;
      case 'expenses': return a.expense_count ?? 0;
      default: return '';
    }
  }, { key: 'name', dir: 'asc' });

  const linkedExpenses = assetDetailQ.data?.expenses ?? [];
  const unlinkedForModal = (unlinkedExpensesQ.data?.expenses ?? []).filter(
    (e) => e.asset_id == null,
  );

  const onLinkExpense = async () => {
    if (expensesAssetId === null || linkExpenseId === '') return;
    setLinkError(null);
    try {
      await updateExpense.mutateAsync({ id: linkExpenseId, asset_id: expensesAssetId });
      setLinkExpenseId('');
      void assetDetailQ.refetch();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setLinkError("You don't have permission to edit expenses.");
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        setLinkError('That expense is on an invoice — void the invoice first.');
        return;
      }
      setLinkError(apiErrorText(err, 'Could not link the expense.'));
    }
  };

  const onUnlinkExpense = async (expenseId: number) => {
    setLinkError(null);
    try {
      await updateExpense.mutateAsync({ id: expenseId, asset_id: null });
      void assetDetailQ.refetch();
    } catch (err) {
      setLinkError(apiErrorText(err, 'Could not unlink the expense.'));
    }
  };

  if (isPending) return <p className="as-status">Loading inventory…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view assets."
        : apiErrorText(error, 'Failed to load assets.');
    return <p className="as-status as-error">{msg}</p>;
  }

  const totalValue = assets
    .reduce((acc, a) => acc + (a.purchase_price ? Number(a.purchase_price) : 0), 0);
  const unassignedCount = assets.filter((a) => a.assigned_to_user_id === null).length;
  const editingAsset =
    editAssetId === null ? null : assets.find((a) => a.id === editAssetId) ?? null;

  return (
    <section className="page-stack page-stack-wide">
      <div className="card as-toolbar">
      <header className="as-header">
        <h2>Asset Inventory</h2>
        <div className="as-header-controls">
          <LookupSelect
            category="asset_type"
            value={typeFilter}
            onChange={setTypeFilter}
            className="form-input as-filter"
            allowEmpty
            emptyLabel="All types"
          />
          <Toggle
            checked={unassignedOnly}
            onChange={setUnassignedOnly}
            label="Unassigned only"
          />
          {can('asset.write') && !showNew && (
            <button
              type="button"
              className="btn-cyan-primary"
              onClick={() => { resetNew(); setShowNew(true); }}
            >
              New asset
            </button>
          )}
        </div>
      </header>
      <div className="as-status-tabs" role="tablist" aria-label="Which assets">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={statusTab === t.key}
            className={statusTab === t.key ? 'as-status-tab is-active' : 'as-status-tab'}
            onClick={() => setStatusTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      </div>

      {rowError && <p className="as-error-banner" role="alert">{rowError}</p>}

      <div className="card">
        <div className="as-summary">
          <div className="as-summary-card">
            <div className="as-summary-label">Total assets</div>
            <div className="as-summary-value">{assets.length}</div>
          </div>
          <div className="as-summary-card">
            <div className="as-summary-label">Unassigned</div>
            <div className="as-summary-value">{unassignedCount}</div>
          </div>
          <div className="as-summary-card">
            <div className="as-summary-label">Purchase value</div>
            <div className="as-summary-value">{formatMoney(totalValue.toFixed(2))}</div>
          </div>
        </div>
      </div>

      {showNew && (
        <div className="card">
        <form className="as-new-form" onSubmit={onCreate}>
          {dupFrom !== null && (
            <p className="as-dup-note" role="status">
              Copied from “{dupFrom}”. The serial number, assignee and linked expense are not
              copied — enter this one’s serial, change anything else, then save it as a new asset.
            </p>
          )}
          <div className="as-new-row">
            <label className="as-field as-field-wide">
              <span>Name *</span>
              <input
                type="text"
                className="form-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Tableau T8u, Atola Insight Forensic, Pelican 1510"
                maxLength={200}
                disabled={createAsset.isPending}
                required
              />
            </label>
            <label className="as-field">
              <span>Type *</span>
              <LookupSelect
                category="asset_type"
                value={newType}
                onChange={setNewType}
                className="form-input"
                disabled={createAsset.isPending}
              />
            </label>
          </div>
          <div className="as-new-row">
            <label className="as-field">
              <span>Serial</span>
              <input
                type="text"
                className="form-input"
                value={newSerial}
                onChange={(e) => setNewSerial(e.target.value)}
                maxLength={120}
                disabled={createAsset.isPending}
              />
            </label>
            <label className="as-field">
              <span>Make / model</span>
              <input
                type="text"
                className="form-input"
                value={newMakeModel}
                onChange={(e) => setNewMakeModel(e.target.value)}
                maxLength={200}
                disabled={createAsset.isPending}
              />
            </label>
          </div>
          <div className="as-new-row">
            <label className="as-field">
              <span>Purchase date</span>
              <DateInput
                value={newPurchaseDate}
                onChange={setNewPurchaseDate}
                disabled={createAsset.isPending}
              />
            </label>
            <label className="as-field">
              <span>Purchase price (USD)</span>
              <MoneyInput
                value={newPurchasePrice}
                onChange={setNewPurchasePrice}
                placeholder="1499.00"
                disabled={createAsset.isPending}
              />
            </label>
            <label className="as-field">
              <span>Warranty expiry</span>
              <DateInput
                value={newWarranty}
                onChange={setNewWarranty}
                disabled={createAsset.isPending}
              />
            </label>
          </div>
          <div className="as-new-row">
            <label className="as-field">
              <span>Location</span>
              <input
                type="text"
                className="form-input"
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                placeholder="e.g. Lab cabinet A3, field kit"
                maxLength={200}
                disabled={createAsset.isPending}
              />
            </label>
            <label className="as-field">
              <span>Assigned to</span>
              <select
                className="form-input"
                value={newAssignee}
                onChange={(e) => {
                  const v = e.target.value;
                  setNewAssignee(v === '' ? '' : Number(v));
                }}
                disabled={createAsset.isPending}
              >
                <option value="">Unassigned</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.display_name}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="as-field as-field-wide">
            <span>Notes</span>
            <textarea
              className="form-input as-textarea"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              rows={2}
              maxLength={10_000}
              disabled={createAsset.isPending}
              placeholder="Optional — calibration history, accessories, etc."
            />
          </label>
          {can('expense.read') && can('expense.write') && (
            <label className="as-field as-field-wide">
              <span>Link purchase expense (optional)</span>
              <select
                className="form-input"
                value={newExpenseId}
                onChange={(e) => {
                  const v = e.target.value;
                  setNewExpenseId(v === '' ? '' : Number(v));
                }}
                disabled={createAsset.isPending || allExpensesForLink.isPending}
              >
                <option value="">— None —</option>
                {expensesForPicker.map((ex) => (
                  <option key={ex.id} value={ex.id}>
                    {fmtDate(ex.date)} · {formatMoney(ex.amount)}
                    {ex.vendor ? ` · ${ex.vendor}` : ''}
                    {ex.notes ? ` — ${ex.notes.slice(0, 40)}` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          {createError && <p className="as-error-banner" role="alert">{createError}</p>}
          <div className="as-new-actions">
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => {
                setShowNew(false);
                resetNew();
              }}
              disabled={createAsset.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-cyan-primary"
              disabled={createAsset.isPending || newName.trim().length === 0}
            >
              {createAsset.isPending ? 'Saving…' : 'Save asset'}
            </button>
          </div>
        </form>
        </div>
      )}

      {assets.length === 0 ? (
        <div className="card">
          <div className="as-empty">
            <p>{emptyText(statusTab, unassignedOnly)}</p>
            {can('asset.write') && statusTab === 'in_inventory' && (
              <p className="as-empty-meta">Click "New asset" to add equipment.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="card card-tight as-table-card table-scroll-card">
        <TableScroll id="assets">
        <table className="as-table">
          <thead>
            <tr>
              <SortableTh column="name" label="Name" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="type" label="Type" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="serial" label="Serial" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="location" label="Location" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="assigned" label="Assigned to" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="warranty" label="Warranty" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="expenses" label="Expenses" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              {(can('asset.assign') || can('asset.delete') || can('asset.write')) && <th></th>}
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((a) => (
              <tr key={a.id}>
                <td>
                  <div className="as-name">{a.name}</div>
                  {a.make_model && (
                    <div className="as-makemodel">{a.make_model}</div>
                  )}
                  {/* Sold to a client through a media charge (0058) — derived
                      from the live time entry, so voiding the invoice turns
                      Sold back into Reserved and deleting the entry frees it. */}
                  {a.sold_time_entry_id != null && (
                    <div className={`as-sold ${a.sold_invoice_id != null ? 'as-sold-billed' : ''}`}>
                      {a.sold_invoice_id != null
                        ? <>Sold · <Link to={`/invoices/${a.sold_invoice_id}`}>{a.sold_invoice_number ?? `Invoice #${a.sold_invoice_id}`}</Link></>
                        : <>Reserved · {a.sold_case_number ?? 'unbilled entry'}</>}
                    </div>
                  )}
                  {/* Left the firm another way (0060). */}
                  {a.disposition !== null && (
                    <div className={`as-sold ${a.disposition === 'sold' ? 'as-sold-billed' : 'as-disposed'}`}
                         title={a.disposal_note ?? undefined}>
                      {a.disposition === 'sold' ? 'Sold' : 'Disposed of'} {fmtDate(a.disposed_at)}
                      {a.disposition === 'sold' && a.disposal_amount !== null && ` · ${formatMoney(a.disposal_amount)}`}
                      {a.disposal_note && <span className="as-disposal-note"> — {a.disposal_note}</span>}
                    </div>
                  )}
                </td>
                <td>{typeLabel(a.asset_type)}</td>
                <td>{a.serial_number ? <code>{a.serial_number}</code> : '—'}</td>
                <td>{a.location ?? '—'}</td>
                <td>
                  {assignBoxFor === a.id ? (
                    <div className="as-assign-row">
                      <select
                        className="form-input as-assign-select"
                        value={assignTarget}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAssignTarget(v === '' ? '' : Number(v));
                        }}
                        disabled={assignAsset.isPending}
                        autoFocus
                      >
                        <option value="">Choose…</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>{u.display_name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-cyan-primary as-mini"
                        onClick={() => onAssign(a.id)}
                        disabled={assignAsset.isPending || assignTarget === ''}
                      >
                        {assignAsset.isPending ? '…' : 'OK'}
                      </button>
                      <button
                        type="button"
                        className="btn-charcoal-secondary as-mini"
                        onClick={() => {
                          setAssignBoxFor(null);
                          setAssignError(null);
                        }}
                        disabled={assignAsset.isPending}
                      >
                        ✕
                      </button>
                    </div>
                  ) : a.assigned_to_user_id ? (
                    <div className="as-assignee-row">
                      <span className="as-assignee-chip">{a.assigned_to_name}</span>
                      {can('asset.assign') && (
                        <button
                          type="button"
                          className="as-link"
                          onClick={() => unassignAsset.mutate(a.id)}
                          disabled={unassignAsset.isPending}
                          title="Return to inventory"
                        >
                          unassign
                        </button>
                      )}
                    </div>
                  ) : can('asset.assign') ? (
                    <button
                      type="button"
                      className="as-link"
                      onClick={() => {
                        setAssignBoxFor(a.id);
                        setAssignTarget('');
                      }}
                    >
                      Assign…
                    </button>
                  ) : (
                    <span className="as-unassigned">Unassigned</span>
                  )}
                </td>
                <td>{fmtDate(a.warranty_expiry)}</td>
                <td>
                  <button
                    type="button"
                    className="as-link"
                    onClick={() => {
                      setExpensesAssetId(a.id);
                      setLinkExpenseId('');
                      setLinkError(null);
                    }}
                  >
                    {(a.expense_count ?? 0) > 0
                      ? `${a.expense_count} linked`
                      : 'Link…'}
                  </button>
                </td>
                <td className="as-actions-cell">
                  {can('asset.write') && a.disposition !== null && (
                    <button
                      type="button"
                      className="as-link"
                      onClick={() => {
                        setRowError(null);
                        restoreAsset.mutate(a.id, {
                          onError: (err) => setRowError(apiErrorText(err, 'Could not return it to inventory.')),
                        });
                      }}
                      disabled={restoreAsset.isPending}
                    >
                      Return to inventory
                    </button>
                  )}
                  {/* A client-sold drive is already sold through its entry. */}
                  {can('asset.write') && a.disposition === null && a.sold_time_entry_id == null && (
                    <button
                      type="button"
                      className="as-link"
                      onClick={() => { setRowError(null); setRetiring(a); }}
                      title="Mark it sold or disposed of — it leaves the inventory list"
                    >
                      Retire…
                    </button>
                  )}
                  {can('asset.write') && (
                    <IconAction
                      action="duplicate"
                      onClick={() => startDuplicate(a)}
                      iconSize={16}
                    />
                  )}
                  {can('asset.write') && (
                    <IconAction
                      action="edit"
                      onClick={() => setEditAssetId(a.id)}
                      iconSize={16}
                    />
                  )}
                  {can('asset.delete') && (
                    <IconAction
                      action="delete"
                      onClick={() => {
                        if (confirm(`Delete "${a.name}"?`)) deleteAsset.mutate(a.id);
                      }}
                      disabled={deleteAsset.isPending}
                      iconSize={16}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
              </TableScroll>
        </div>
      )}
      {assignError && (
        <div className="card">
          <p className="as-error-banner" role="alert">{assignError}</p>
        </div>
      )}

      {expensesAssetId !== null && (
        <Modal
          open
          title={`Expenses — ${assets.find((x) => x.id === expensesAssetId)?.name ?? 'asset'}`}
          onClose={() => setExpensesAssetId(null)}
          busy={updateExpense.isPending}
          footer={
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => setExpensesAssetId(null)}
            >
              Close
            </button>
          }
        >
          {assetDetailQ.isPending ? (
            <p className="as-status">Loading…</p>
          ) : linkedExpenses.length === 0 ? (
            <p className="as-empty-meta">No expenses linked yet.</p>
          ) : (
            <ul className="as-expense-list">
              {linkedExpenses.map((ex) => (
                <li key={ex.id} className="as-expense-list-item">
                  <div>
                    <strong>{fmtDate(ex.date)}</strong>
                    {' · '}
                    {formatMoney(ex.amount)}
                    {ex.vendor ? ` · ${ex.vendor}` : ''}
                    {ex.notes ? (
                      <div className="as-makemodel">{ex.notes.slice(0, 120)}</div>
                    ) : null}
                  </div>
                  {can('expense.write') && (
                    <IconAction
                      action="delete"
                      label="Unlink"
                      title="Unlink expense from this asset"
                      onClick={() => void onUnlinkExpense(ex.id)}
                      disabled={updateExpense.isPending}
                      iconSize={16}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}

          {can('expense.write') && can('expense.read') && (
            <div className="as-link-expense">
              <label className="as-field as-field-wide">
                <span>Link another expense</span>
                <div className="as-assign-row">
                  <select
                    className="form-input"
                    value={linkExpenseId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setLinkExpenseId(v === '' ? '' : Number(v));
                    }}
                    disabled={updateExpense.isPending || unlinkedExpensesQ.isPending}
                  >
                    <option value="">Choose unlinked expense…</option>
                    {unlinkedForModal.map((ex) => (
                      <option key={ex.id} value={ex.id}>
                        {fmtDate(ex.date)} · {formatMoney(ex.amount)}
                        {ex.vendor ? ` · ${ex.vendor}` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-cyan-primary as-mini"
                    onClick={() => void onLinkExpense()}
                    disabled={updateExpense.isPending || linkExpenseId === ''}
                  >
                    Link
                  </button>
                </div>
              </label>
            </div>
          )}
          {linkError && <p className="as-error-banner" role="alert">{linkError}</p>}
        </Modal>
      )}

      {retiring && (
        <RetireAssetModal asset={retiring} onClose={() => setRetiring(null)} />
      )}

      {editingAsset && (
        <EditAssetModal
          open
          asset={editingAsset}
          onClose={() => setEditAssetId(null)}
        />
      )}
    </section>
  );
}
